import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, CloseIcon, Combobox, Loader, TextInput, useCombobox } from '@any-ui-react/core';
import { useUncontrolled } from '@mantine/hooks';

import { DefaultOperator } from '../../condition-builder';
import { ValueEditorProps, useValueEditor } from '../../condition-builder';
import { useConditionBuilderStore } from '../../condition-builder/store';
import { Option, RuleType } from '../../condition-builder/types';
import { DEFAULT_RULE } from '../../condition-builder/utils';

/**
 *
 * TODO: create standalone Tag component in feature-crm and share usage to function-chat
 * remove libs/function-chat/src/lib/modules/shared/components/tag/Tag.tsx
 *
 * https://anymindgroup.atlassian.net/browse/AC-4924
 */
const Tag = ({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  count?: number;
  hideCount?: boolean;
  onRemove?: () => void;
  size?: 'md' | 'sm';
  disabled?: boolean;
}) => {
  return (
    <div className="text-2xs text-gray-9 group flex h-full max-w-max items-center">
      <div className="bg-gray-1 group-hover:bg-gray-2 flex min-w-0 flex-1 items-center rounded px-2 py-1 leading-none">
        <span className="min-w-0 flex-1 truncate">{label}</span>

        {onRemove && !disabled && (
          <button
            className="ml-1 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center"
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={onRemove}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
};

const ComboboxOptions = ({
  children,
  loading,
}: {
  children: React.ReactNode;
  loading?: boolean;
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-1">
        <Loader size="xs" />
      </div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export const TagSelectValueEditor = (props: ValueEditorProps) => {
  const { t } = useTranslation(['crm']);
  const { baseProps, getErrorMessage } = useValueEditor(props);
  const state = useConditionBuilderStore((state) => state);

  const combobox = useCombobox();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);

  const [selected, setSelected] = useUncontrolled<string[]>({
    value: props.values || DEFAULT_RULE.values,
    onChange: props.onChange,
    defaultValue: DEFAULT_RULE.values,
  });

  const handleSelect = (option: Option) => {
    if (selected.includes(option.value)) {
      const newSelected = selected.filter((s: string) => s !== option.value);

      const finalSelected = newSelected.length === 0 ? DEFAULT_RULE.values : newSelected;

      setSelected(finalSelected);
      props.onChange?.(finalSelected);
    } else {
      const newSelected = [...selected, option.value].filter((s: string) => s !== '');
      setSelected(newSelected);
      props.onChange?.(newSelected);
    }
  };

  const filteredOptions = useMemo(() => {
    const usedCustomerTagValues = new Set<string>();

    state.condition.rules.forEach((rule: RuleType<string, string, string[]>) => {
      if (rule.field === 'CUSTOMER_TAGS' && rule.values) {
        rule.values.forEach((value: string) => {
          if (value) {
            usedCustomerTagValues.add(value);
          }
        });
      }
    });

    return options
      .filter((option) => {
        if (selected.includes(option.value)) {
          return true;
        }
        return !usedCustomerTagValues.has(option.value);
      })
      .filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search, selected, state.condition.rules]);

  useEffect(() => {
    setLoading(true);

    const loadOptions = async () => {
      if (props.field?.onLoadOptions) {
        const loadOptionsFn = props.field.onLoadOptions();
        const options = (await loadOptionsFn()) || [];
        setOptions(options);
      }
      setLoading(false);
    };
    loadOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = getErrorMessage();

  // Check for NULL and NOT_NULL operators - return null for these cases
  if (
    ([DefaultOperator.enum.NULL, DefaultOperator.enum.NOT_NULL] as string[]).includes(
      props.operator
    )
  ) {
    return null;
  }

  return (
    <div className={baseProps.className}>
      <div className="flex flex-col">
        <Combobox store={combobox}>
          <Combobox.Target withAriaAttributes={false}>
            <TextInput
              placeholder={t('crm.fields.recipients.tags.placeholder', { ns: 'crm' })}
              value={search}
              rightSection={
                <div className="h-[18px] w-[18px] pr-[3px]">
                  <Combobox.Chevron className="text-gray-7" />
                </div>
              }
              classNames={{ input: 'w-full', section: 'mr-px' }}
              onFocus={() => combobox.toggleDropdown()}
              onBlur={() => {
                setSearch('');
                combobox.closeDropdown();
              }}
              onChange={(e) => setSearch(e.target.value)}
              error={error}
              disabled={props.disabled}
            />
          </Combobox.Target>

          <Combobox.Dropdown className="max-h-52 overflow-y-auto">
            <ComboboxOptions loading={loading}>
              {filteredOptions.map((option) => (
                <Combobox.Option
                  key={option.value}
                  className="flex h-10 items-center gap-2"
                  value={option.value}
                  onClick={() => handleSelect(option)}
                >
                  <Checkbox readOnly checked={selected.includes(option.value)} />
                  {option.label}
                </Combobox.Option>
              ))}
            </ComboboxOptions>
          </Combobox.Dropdown>
        </Combobox>

        <div className="mt-1 flex flex-wrap gap-1">
          {selected
            .filter((optionValue) => !!optionValue)
            .map((optionValue) => {
              const option = options.find((opt) => opt.value === optionValue);
              return (
                <Tag
                  key={optionValue}
                  label={option?.label || optionValue}
                  onRemove={() => {
                    const newSelected = selected.filter((s: string) => s !== optionValue);
                    setSelected(newSelected);
                    props.onChange?.(newSelected);
                  }}
                  disabled={props.disabled}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};
